<template>
  <el-card :key="facility"
    v-loading="loading"
    shadow="never"
  >
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item
          v-if="$root.state.facility"
          :to="{ name: 'Facility Sitemap' }"
        >
          {{String($root.state.facilityData.code).toUpperCase()}} Sitemap
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <div>
      <el-popover
        v-if="hoveredUnit"
        :width="280"
        trigger="manual"
        :style="computedStyle"
        v-model="showpop">
      <!-- :style="{position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, margin: '0 auto'}" -->
        <div class="descriptions">
          <div class="description unit-name">{{ hoveredUnit.name }}</div>
          <div class="description">
            <span class="label">Status: </span>
            <span>{{ hoveredUnit.status | printableStatus }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.leaseStatus && (hoveredUnit.status !== 'AVAILABLE' && hoveredUnit.status !== 'UNAVAILABLE')">
            <span class="label">Finance: </span>
            <span>{{ hoveredUnit.leaseStatus | printableStatus }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.status !== 'AVAILABLE' && customers && hoveredUnit.rental && hoveredUnit.rental.customer && customers[hoveredUnit.rental.customer].company">
            <span class="label">Occupant: </span>
            <span>{{ customers[hoveredUnit.rental.customer].company }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.rental && hoveredUnit.rental.moveOut">
            <span class="label">Move out date: </span>
            <span>{{ hoveredUnit.rental.moveOut | formatDate }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.description">
            <span class="label">Description:</span>
            <span>{{ hoveredUnit.description }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.computedSize">
            <span class="label">Size: </span>
            <span>{{hoveredUnit.computedSize }} Sq. ft.</span>
          </div>
          <div class="description" v-if="hoveredUnit.tags && JSON.stringify(hoveredUnit.tags) !== '[]'">
            <span class="label">Tags: </span>
            <div class="tags">
              <el-tag size="mini" class="tag" :key="tag.value" v-for="tag in hoveredUnit.tags">
                {{ tag.value }}
              </el-tag>
            </div>
          </div>
          <div class="description">
            <span class="label">Asking Price: </span>
            {{ price | currencySign($root.state.facilityData) }}
            <span v-if="securityFeeEnabled">
              + {{ computeSecurityFee(hoveredUnit.computedSize) | currencySign($root.state.facilityData) }}
            </span>
          </div>
          <div class="description" v-if="hoveredUnit.status !== 'AVAILABLE'">
            <span class="label">Past Due: </span>
            <span>{{ dueAmount | currencySign(facilityData) }}</span>
          </div>
          <div class="description" v-if="hoveredUnit.customer && customers[hoveredUnit.customer].ach">
            <span class="label">Pend ACH:</span>
            <span>{{ customers[hoveredUnit.customer].ach | currencySign(facilityData) }}</span>
          </div>
        </div>
      </el-popover>
      <div class="sitemap-controls">
        <el-switch
          v-model="enableLeaseStatus"
          active-text="Finance Status"
          inactive-text="Leasing Status"
        ></el-switch>
        <div>
          <el-button type="primary" :icon="editingMap ? 'el-icon-check' : 'el-icon-edit'" size="small" v-if="hasAccess" @click="switchMapToEditing">
            {{editButtonText}}
          </el-button>
          <el-button icon="el-icon-plus" size="small" v-if="hasAccess && editingMap" @click="addUnit">
            Add Unit
          </el-button>
          <el-button icon="el-icon-download" size="small" :loading="pdfLoading" @click="saveToPdf">
            Save to PDF
          </el-button>
          <el-popover
              placement="right"
              :width="700"
              trigger="hover"
            >
              <el-button size="small" slot="reference" icon="el-icon-question">Legend</el-button>
              <LegendSiteMap
                :facility-data="facilityData"
                v-if="org"
                v-show="org"
                :org="org"
                :parkingPricing="{ ...facilityData.parkingPricing }"
                :leaseStatus="enableLeaseStatus"
                :key="enableLeaseStatus"
              />
          </el-popover>
        </div>
        <el-switch
          v-model="enableParking"
          active-text="View all"
          inactive-text="Disable Parking"
          @change="getUnits"
        ></el-switch>
      </div>
      <v-stage
        v-if="ready"
        id="sitemap"
        ref="stage"
        :config="configKonva"
        :style="{backgroundColor: stageBackground}"
        @dragmove="handleDragstart"
        @dragend="handleDragend"
      >
        <v-layer ref="number-layer" v-if="units">
          <v-group :key="item._id" :ref="item._id"
            v-for="item in units"
            @click="routeToUnit"
            :config="{
              draggable: canDrag,
              type: 'group',
              x: item.x && item.x < configKonva.width ? item.x : (configKonva.width / 2),
              y: item.y && item.y < configKonva.height ? item.y : (configKonva.height / 2),
              }"
              @mouseenter="handleMouseMove"
              @mouseleave="closepopup"
          >
            <v-rect
              :key="`${item._id}-unit`"
              :ref="`${item._id}-unit`"
              :config="{
                // x: item.x || (configKonva.width / 2),
                // y: item.y || (configKonva.height / 2),
                id: item._id,
                width: item.width < 50 ? 50 : item.width,
                height: item.length < 50 ? 50 : item.length,
                fill: getUnitColor(enableLeaseStatus ? item.leaseStatus : item.status),
                custom: item._id,
                type: 'rect',
              }"
            >
            </v-rect>
            <v-rect
              v-if="editingMap"
              :key="`${item._id}-border`"
              :config="{
                // x: item.x || (configKonva.width / 2),
                // y: item.y || (configKonva.height / 2),
                id: item.id,
                width: item.width < 50 ? 50 : item.width,
                height: item.length < 50 ? 50 : item.length,
                custom: item._id,
                stroke: '#efefef',
                strokeWidth: 1.2,
                type: 'rect',
              }"
            >
            </v-rect>
            <v-text
              :key="`${item._id}-number`"
              :config="{
                id: item.id,
                text: item.name,
                fontSize: item.name.length > 2 ? 12 : 16,
                fill: '#FEFEFE',
                type: 'text',
                custom: item._id,
                verticalAlign: 'top',
                fontWeight: 'bold',
                padding: 5,
                lineHeight: 1,
                ellipsis: true,
                width: item.width < 50 ? 50 : item.width,
                height: 20,
              }"
            >
                <!-- shadowColor: 'black',
                shadowBlur: 10,
                shadowOpacity: 0.6, -->
            </v-text>
            <v-text
              :key="`${item._id}-size`"
              :config="{
                y: 13,
                id: item.id,
                text: `${item.computedSize || (item.length * item.width)}`,
                fontSize: 11,
                fill: '#FFF',
                type: 'text',
                custom: item._id,
                verticalAlign: 'middle',
                padding: 5,
                lineHeight: 1,
                ellipsis: true,
                width: item.width < 50 ? 50 : item.width,
                height: 40
              }"
            >
            </v-text>
          </v-group>
        </v-layer>
      </v-stage>
    </div>
    <el-dialog
      title="New Unit"
      :visible.sync="dialogFormVisible"
      :close-on-click-modal="false"
    >
      <NewSiteUnitType
        @unit-added="unitAdded"
        :max-width="configKonva.width"
        :max-height="configKonva.height"
      />
    </el-dialog>
    <!-- :offset="hoveredUnit.x" -->
  </el-card>
</template>

<script>
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { UNITS_API_BASE, SITEMAP_API, SITEMAP_API_UPDATE, ORG_API, ADMIN_API_BASE, LEASING_API, CUSTOMER_API_BASE, UNIT_RENTAL_API_BASE } from '../env';
import NewSiteUnitType from './NewSiteUnitType.vue';
import SiteMapSettings from './SiteMapSettings.vue';
import { computeUnitPricing, round } from '../utils/pricing';
import LegendSiteMap from './LegendSiteMap.vue';

export default {
  name: 'SiteMap',
  components: { NewSiteUnitType, SiteMapSettings, LegendSiteMap },
  props: { facility: { type: String } },
  watch: {
    async facility() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }

      this.loading = true;

      await this.getFacility();
      await Promise.all([
        this.getSitemap(),
        this.getUnits().then(() => {
          this.getFacilityLeases();
          this.getFacilityCustomers();
        }),
        this.getOrg(),
      ]);
      this.setCanvasSize();
      this.loading = false;
    },
  },
  async mounted() {
    try {
      this.abortController = new AbortController();
      this.loading = true;

      await this.getFacility();
      await Promise.all([
        this.getSitemap(),
        this.getUnits().then(() => {
          this.getFacilityLeases();
          this.getFacilityCustomers();
        }),
        this.getOrg(),
      ]);
      this.setCanvasSize();
      this.ready = true;
    } catch (err) {
      console.error(err);
      this.ready = false;
    } finally {
      this.loading = false;
    }
  },
  data() {
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;
    return {
      customers: {},
      abortController: null,
      sitemap: null,
      facilityData: null,
      org: null,
      loading: false,
      ready: false,
      editingMap: false,
      dragItemId: null,
      stage: null,
      layer: null,
      leases: [],
      unitRentals: [],
      showSubmenu: false,
      dialogFormVisible: false,
      units: [],
      refUnits: [],
      stageBackground: 'rgb(255,255,204)',
      showpop: false,
      pdfLoading: false,
      hoveredUnit: { x: canvasWidth / 2, y: canvasHeight / 2 },
      configKonva: {
        width: canvasWidth,
        height: canvasHeight,
      },
      enableParking: false,
      enableLeaseStatus: false,
    };
  },
  methods: {
    computeSecurityFee(size) {
      if (this.securityFeeEnabled) {
        return round((this.facilityData.securityFee.fee / 100) * size);
      }
      return 0;
    },
    setCanvasSize() {
      console.log('setting canvas size')
      // Don't change the canvas size if there's no units
      if (!this.units || this.units.length === 0) {
        return;
      }
      const maxWidth = Math.max(...this.units.map(unit => unit.x || 0)) + 250;
      const maxHeight = Math.abs(Math.max(...this.units.map(unit => unit.y || 0))) + 150;
      const zoom = ((window.outerWidth - 10) / window.innerWidth);

      if (Number.isFinite(maxWidth)) {
        this.configKonva.width = Math.max(this.configKonva.width, maxWidth * zoom);
      }
      if (Number.isFinite(maxHeight)) {
        this.configKonva.height = Math.max(this.configKonva.height, maxHeight * zoom);
      }
    },
    async getOrg() {
      try {
        const org = await axios.get(`${ORG_API}/theme`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.org = {
          colors: org.data,
        };
      } catch (err) {
        this.org = {
          colors: [],
        };
        this.$message.warning({
          title: 'Warning',
          message: 'Org theme not accessible',
        });
      }
    },
    async switchMapToEditing() {
      if (this.editingMap) {
        this.ready = false;
        for (const unit of this.refUnits) {
          try {
            await axios.put(`${UNITS_API_BASE}/unit/${unit._id}`, {
              x: unit.x,
              y: unit.y,
            }, {
              headers: {
                Authorization: `Bearer ${this.$keycloak.token}`,
              },
            });
          } catch (err) {
            this.$notify.error({
              title: 'Error',
              message: 'Error updating the new position of a unit.',
            });
          }
        }
        this.refUnits = [];
        await this.getUnits();
        this.ready = true;
      }
      this.editingMap = !this.editingMap;
    },
    getUnitColor(status) {
      if (!status) {
        return '#035AA6';
      }
      const [theme] = this.org.colors.filter((item) => {
        if (item.type === status) {
          return item.color;
        }
      });
      return theme ? theme.color : '#035AA6';
    },
    async getUnitRentals(unitIds) {
      const response = await axios.get(`${UNIT_RENTAL_API_BASE}/${this.facility}/units`, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
        params: {
          units: unitIds.toString(),
        },
      });
      return response.data.result;
    },
    async getUnits() {
      const units = await axios.get(`${UNITS_API_BASE}/${this.facility}`, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
        params: {
          limit: 250,
          populate: 'invoice',
          types: this.enableParking ? '' : 'Warehouse,Office,Other',
        },
      });

      this.units = units.data;
    },
    async getFacility() {
      const response = await axios.get(`${ADMIN_API_BASE}/facilities/${this.facility}`, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.facilityData = response.data;
      if (this.facilityData.enableSceneSitePlan === true) {
        this.$router.push(`/sitemap/${this.facility}`);
      }
    },
    async getSitemap() {
      const sitemapApi = SITEMAP_API(this.facility);
      const sitemap = await axios.get(`${sitemapApi}/`, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.sitemap = sitemap.data[0];
    },
    async updateTheme(theme) {
      const sitemapApi = `${SITEMAP_API_UPDATE}/${this.sitemap._id}`;
      await axios.put(`${sitemapApi}`, {
        colors: theme,
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      await this.getSitemap();
    },
    routeToHomePage() {
      this.$router.push('/');
    },
    addUnit() {
      if (!this.facility) {
        this.$notify.error({
          title: 'Error',
          message: 'Please select the facility from the top.',
        });
        return;
      }
      this.dialogFormVisible = true;
    },
    async unitAdded(unit) {
      try {
        this.dialogFormVisible = false;

        await axios.post(`${UNITS_API_BASE}/unit`, { ...unit, facility: this.facility, size: Number(unit.size) }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        await this.getUnits();
      } catch (err) {
        console.err(err);
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    handleDragstart(e) {
    },
    handleDragend(e) {
      // const stage = e.target.getStage();
      // const pos = stage.getPointerPosition(); // don't get this x and y. Use from get node()
      const unit = e.target.children[0].attrs;
      const [unitRect] = this.$refs[`${unit.custom}-unit`];
      const node = unitRect.getNode();
      const parentGroupAttributes = node.parent.attrs;
      const { x, y } = parentGroupAttributes;
      const removedMovingUnitFromTheRef = this.refUnits.filter(item => item._id !== unit.custom);
      removedMovingUnitFromTheRef.push({ _id: unit.custom, x, y });
      this.refUnits = [...removedMovingUnitFromTheRef];
    },
    handleMouseMove(e) {
      if (this.editingMap) return;
      if (e.target.attrs.type !== 'text') {
        return;
      }
      const [hoveredUnit] = this.units.filter(unit => unit._id === e.target.attrs.custom);
      this.hoveredUnit = hoveredUnit;

      if (hoveredUnit.invoice && hoveredUnit.customer) {
        const rentalFound = this.unitRentals.length ? this.unitRentals.find(
          rental => rental.unitInvoice === hoveredUnit.invoice._id && rental.customer === hoveredUnit.customer,
        ) : null;
        if (rentalFound) {
          this.hoveredUnit.rental = rentalFound;
        }
      }

      this.showpop = true;
    },
    routeToUnit(e) {
      if (this.editingMap) return;
      const unitId = e.target.attrs.custom;
      this.$router.push({ name: 'Facility Unit', params: { facility: this.facility, unit: unitId } });
    },
    closepopup(e) {
      if (this.editingMap) return;
      if (e.target.attrs.type !== 'text') {
        return;
      }
      this.showpop = false;
      this.hoveredUnit = {};
    },
    async saveToPdf() {
      this.pdfLoading = true;
      const stage = this.$refs.stage.getStage();
      const facilityId = this.facility;
      stage.toDataURL({
        pixelRatio: 2,
        async callback(url) {
          const pdf = new jsPDF('p', 'mm', 'a4');
          pdf.text(60, 10, 'Readyspaces facility sitemap.');
          pdf.addImage(url, 'PNG', 0, 30, window.innerWidth / 6, window.innerHeight / 5);
          await pdf.save(`${facilityId}.pdf`, { returnPromise: true });
        },
      });
      this.pdfLoading = false;
    },
    async getFacilityLeases() {
      this.leases = this.units.filter(unit => unit.invoice).map(unit => unit.invoice);
      this.unitRentals = [];
      for (const lease of this.leases) {
        this.unitRentals.push(...lease.unitRentals)
      }
    },
    async getFacilityCustomers() {
      try {
        const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v2/facility/${this.facility}`, {
          signal: this.abortController.signal,
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        const unitsCustomers = new Set(this.units.map(unit => unit.customer));
        response.data = response.data.filter(customer => customer && unitsCustomers.has(customer._id));

        for (const customer of response.data) {
          if (this.customers && customer._id && this.customers[customer._id] !== undefined) { continue; }

          const currentDate = new Date();
          const currentMonth = currentDate.getMonth() + 1;
          const currentYear = currentDate.getFullYear();
          customer.ach = 0;
          if (customer.achTransactions && customer.achTransactions.length) {
            customer.ach = customer.achTransactions
              .map(transaction => transaction.amount)
              .reduce((pv,nx) => pv + nx, 0);
          }

          const due = customer.amountsByPeriod && customer.amountsByPeriod.length ? customer.amountsByPeriod.filter(item => item.year < currentYear || (item.year === currentYear && item.month <= currentMonth)).map(transaction => transaction.total).reduce((pv,nx) => pv + nx, 0) : 0;
          customer.due = due < 0 ? 0 : due;

          this.customers[customer._id] = customer;
        }
      } catch (err) {
          console.error(err);
          this.$message.error({
            title: 'Error',
            message: 'Unable to load customers data',
          });
      }
    },
  },
  computed: {
    securityFeeEnabled() {
      return this.facilityData && this.facilityData.securityFee && this.facilityData.securityFee.enabled && this.facilityData.securityFee.fee > 0;
    },
    hasAccess() {
      const roles = this.$keycloak.tokenParsed.realm_access.roles;
      if (roles.includes('level_2') || roles.includes('level_3') || roles.includes('level_1')) {
        return true;
      }
      return false;
    },
    editButtonText() {
      if (this.editingMap) {
        return 'Done';
      }
      return 'Edit Map';
    },
    canDrag() {
      if (this.editingMap) {
        return true;
      }
      return false;
    },
    price() {
      if (this.hoveredUnit && this.facilityData) {
        return computeUnitPricing(this.hoveredUnit, this.facilityData);
      }

      return 0;
    },
    dueAmount() {
      if (!this.hoveredUnit || !this.hoveredUnit.invoice) { return 0; }

      const matchedLease = this.leases.filter(item => item._id === this.hoveredUnit.invoice._id);
      return matchedLease.length > 0 ? Math.abs((matchedLease[0].amountToBePaid ? matchedLease[0].amountToBePaid : matchedLease[0].amount) - matchedLease[0].amountPaid) : 0;
    },
    computedStyle() {
      return {
        ...(
          this.hoveredUnit.y ? {
            ...(this.configKonva.height / 2 > this.hoveredUnit.y ? {
              top: `${this.hoveredUnit.y + Math.min((this.configKonva.height / 2) + this.hoveredUnit.y, 250)}px`,
            } : {
              top: `${this.hoveredUnit.y - Math.min(this.configKonva.height - this.hoveredUnit.y, 250)}px`,
            }),
            // top: this.configKonva.height / 2 > this.hoveredUnit.y ? `${this.hoveredUnit.y + Math.min((this.configKonva.height / 2) - this.hoveredUnit.y, 200)}px` : `${this.hoveredUnit.y - Math.min(this.configKonva.height - this.hoveredUnit.y, 200)}px`,
          } : {}
        ),
        ...(
          this.hoveredUnit.x ? {
            ...(this.configKonva.width / 2 < this.hoveredUnit.x ? {
              left: `${this.hoveredUnit.x - Math.min(this.hoveredUnit.x - (this.configKonva.width / 2), 200)}px`,
            } : {
              left: `${Math.min((this.configKonva.width / 2) - this.hoveredUnit.x, 200) + this.hoveredUnit.x}px`, //
            }),
            // left: this.configKonva.width / 2 < this.hoveredUnit.x ? `${(this.hoveredUnit.x - (this.hoveredUnit.x - (this.configKonva.width / 2)))}px` : `${this.hoveredUnit.x + ((this.configKonva.width / 2) - this.hoveredUnit.x)}px`,
          } : {}
        ),
        position: 'absolute',
        zIndex: this.hoveredUnit.name ? 1 : -1,
      };
    },
  },
};
</script>

<style scoped>
#sitemap {
  /* background-color: #587291; */
  /* background-color: #A2DBFA; */
  /* background-color: #120136; */
  width: 100%;
  overflow: auto;
}
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-content: space-between;
  padding: 5px 0;
}
.descriptions {
  display: flex;
  align-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.description {
  border-radius: 5px;
  flex-grow: 1;
  flex-shrink: 2;
  border: 1px solid #f0f0f0;
  padding: 5px 10px;
  align-self: stretch;
}

.description .label {
  font-weight: bold;
}
.submenu {
  display: none;
  position: absolute;
  width: 60px;
  background-color: white;
  box-shadow: 0 0 5px grey;
  border-radius: 3px;
}
.unit-name {
  font-size: 1rem;
  font-weight: bold;
  border-color: #fff;
}

.sitemap-controls {
  padding-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/SiteMap.vue